import { coco, ResourceType } from '@cotera/api';
import type { InitialAction } from '@cotera/sdk/core';
import { z } from 'zod';
import type { PendingChatSubmission } from '../../chat/models/pending-chat-submission';
import type {
  ChatSeedBuildInput,
  ChatSeedStrategy,
} from '../../chat-seed/types';
import { agentChatSeedChrome } from './chrome';
import { agentChatSeedPreSubmit } from './pre-submit';

const {
  COCO_CREATE_AGENT_TOOL_KEY,
  COCO_EDIT_AGENT_TOOL_KEY,
  COCO_EDIT_STRUCTURED_OUTPUT_TOOL_KEY,
  COCO_READ_DOCUMENT_TOOL_KEY,
} = coco;

const uuidSchema = z.string().uuid();

export const AGENT_SEED_TYPE = 'agent';

export const NEW_AGENT_CHAT_DISPLAY_TITLE = 'New Agent';

export function defaultAgentKey(agentId: string): string {
  return `agent-${agentId.replace(/-/g, '')}`;
}

function parseArtifactId(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get('artifactId');
  if (raw === null) {
    return null;
  }
  const parsed = uuidSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export const agentChatSeedStrategy: ChatSeedStrategy = {
  type: AGENT_SEED_TYPE,
  artifactResourceType: ResourceType.Agent,
  getChatChrome: () => agentChatSeedChrome,
  preSubmit: agentChatSeedPreSubmit,

  buildUrlSearchParams: (input: ChatSeedBuildInput): URLSearchParams => {
    const params = new URLSearchParams();
    params.set('seed', AGENT_SEED_TYPE);
    params.set('artifactId', input.artifactId);
    if (input.isNew === true) {
      params.set('new', '1');
    }
    if (input.folderId !== undefined) {
      params.set('folderId', input.folderId);
    }
    return params;
  },

  buildPendingContext: (input: ChatSeedBuildInput) => {
    const agentKey = input.agentKey ?? defaultAgentKey(input.artifactId);
    const initialActions: InitialAction[] = [
      { type: 'focus_agent', agentId: input.artifactId },
    ];
    // The agent itself is not seeded as a tool key, and the chat is not linked
    // via `agentId` — either would make this a chat *with* the agent. The
    // `focus_agent` action below attaches the agent and links it to the chat.
    // Seed the granular agent-editing tools; loading any of them activates the
    // `coco/edit-agents` skill, which supplies the full editing toolkit and
    // instructions (no editor sub-agent).
    const initialToolKeys: string[] = [
      COCO_CREATE_AGENT_TOOL_KEY,
      COCO_EDIT_AGENT_TOOL_KEY,
      COCO_EDIT_STRUCTURED_OUTPUT_TOOL_KEY,
      COCO_READ_DOCUMENT_TOOL_KEY,
    ];
    const data: Record<string, unknown> = {
      agentId: input.artifactId,
      agentKey,
      ...(input.isNew === true && { isNew: true }),
      // This is the chat's top-level agent, so surface the documents/agents it
      // @-mentions as sibling artifact tabs. Sub-agents opened as their own tabs
      // don't carry this flag, so their references aren't expanded in turn.
      expandReferences: true,
    };

    return {
      ...(input.isNew === true && {
        displayTitle: NEW_AGENT_CHAT_DISPLAY_TITLE,
      }),
      initialArtifacts: [
        {
          artifactType: 'agent' as const,
          artifactId: input.artifactId,
          data,
        },
      ],
      initialToolKeys,
      initialActions,
    };
  },

  parseFromUrl: (
    searchParams: URLSearchParams
  ): PendingChatSubmission | null => {
    if (searchParams.get('seed') !== AGENT_SEED_TYPE) {
      return null;
    }

    const artifactId = parseArtifactId(searchParams);
    if (artifactId === null) {
      return null;
    }

    const isNew = searchParams.get('new') === '1';

    return {
      ...agentChatSeedStrategy.buildPendingContext({ artifactId, isNew }),
      seedOnly: true,
    };
  },
};
